/**
 * Экспорт всех предустановок Git-репозиториев
 */

export type {
  GitPreset,
  PresetFile,
  PresetCommit,
  PresetBranch,
  PresetRemote,
  VisualizationMeta,
  LessonPresets,
  PresetFactory,
} from './types';

// Предустановки
export { simpleProjectPreset } from './simple-project';
export { featureBranchPreset } from './feature-branch';
export { mergeConflictPreset, featureConfigContent, resolvedConfigContent } from './merge-conflict';
export { gitFlowPreset } from './git-flow';
export { rebaseScenarioPreset, afterRebaseState } from './rebase-scenario';
export { remoteSyncPreset, afterSyncState } from './remote-sync';

import type { GitPreset } from './types';
import { simpleProjectPreset } from './simple-project';
import { featureBranchPreset } from './feature-branch';
import { mergeConflictPreset } from './merge-conflict';
import { gitFlowPreset } from './git-flow';
import { rebaseScenarioPreset } from './rebase-scenario';
import { remoteSyncPreset } from './remote-sync';

/**
 * Карта всех предустановок по ID
 */
export const allPresets: Record<string, GitPreset> = {
  'simple-project': simpleProjectPreset,
  'feature-branch': featureBranchPreset,
  'merge-conflict': mergeConflictPreset,
  'git-flow': gitFlowPreset,
  'rebase-scenario': rebaseScenarioPreset,
  'remote-sync': remoteSyncPreset,
};

/**
 * Получить предустановку по ID
 */
export function getPresetById(id: string): GitPreset | undefined {
  return allPresets[id];
}

/**
 * Получить список всех доступных предустановок
 */
export function getAvailablePresets(): Array<{ id: string; name: string; description: string }> {
  return Object.values(allPresets).map((preset) => ({
    id: preset.id,
    name: preset.name,
    description: preset.description,
  }));
}

/**
 * Маппинг предустановок к урокам
 * Ключи - ID уроков, значения - ID предустановок
 */
export const lessonPresetMapping: Record<string, string> = {
  // Модуль 1: Основы Git (пустой проект)
  '1-1': 'simple-project', // Что такое Git?
  '1-2': 'simple-project', // Инициализация репозитория
  '1-3': 'simple-project', // Три дерева Git
  '1-4': 'simple-project', // Индекс: глубокое погружение

  // Модуль 2: Базовые команды Git
  '2-1': 'simple-project', // git add
  '2-2': 'simple-project', // git commit
  '2-3': 'simple-project', // git status
  '2-4': 'simple-project', // git log
  '2-5': 'simple-project', // git diff
  '2-6': 'simple-project', // git rm и git mv

  // Модуль 3: Ветвление и слияние
  '3-1': 'feature-branch', // Что такое ветки?
  '3-2': 'feature-branch', // Создание и переключение веток
  '3-3': 'feature-branch', // Слияние веток
  '3-4': 'merge-conflict', // Конфликты слияния
  '3-5': 'feature-branch', // Fast-forward merge

  // Модуль 4: Переписывание истории
  '4-1': 'rebase-scenario', // git rebase
  '4-2': 'rebase-scenario', // Interactive rebase
  '4-3': 'simple-project', // git amend
  '4-4': 'simple-project', // git reset
  '4-5': 'simple-project', // git cherry-pick

  // Модуль 5: Удалённые репозитории
  '5-1': 'remote-sync', // Что такое remote?
  '5-2': 'remote-sync', // git clone
  '5-3': 'remote-sync', // git fetch
  '5-4': 'remote-sync', // git pull
  '5-5': 'remote-sync', // git push

  // Модуль 6: Продвинутые техники
  '6-1': 'git-flow', // Git Flow
  '6-2': 'git-flow', // GitHub Flow
  '6-3': 'git-flow', // GitLab Flow
  '6-4': 'git-flow', // Лучшие практики
};

/**
 * Получить предустановку для урока
 */
export function getPresetForLesson(lessonId: string): GitPreset | undefined {
  const presetId = lessonPresetMapping[lessonId];
  if (!presetId) return undefined;
  return getPresetById(presetId);
}
